import { MENU_CHANNELS, ORDER_CHANNELS } from './menuChannels';

export const SERVICE_TYPE_BY_MENU_CHANNEL = {
  dinein: 'DINEIN',
  myeats: 'MYEATS',
  jreats: 'JREATS',
  catering: 'CATERING',
};

export const SERVICE_TYPE_BY_ORDER_CHANNEL = {
  offline: 'DINEIN',
  myeats: 'MYEATS',
  jreats: 'JREATS',
  catering: 'CATERING',
  partyHall: 'PARTY_HALL',
};

export const SERVICE_TYPE_BY_DELIVERY_SOURCE = {
  MYEATS: 'MYEATS',
  JREATS: 'JREATS',
};

/** Active opted-in service types, or null when filtering should not apply. */
export function extractEnabledServiceTypes(services) {
  if (!Array.isArray(services) || services.length === 0) return null;

  const types = services
    .filter((row) => String(row.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE')
    .map((row) => String(row.serviceType ?? row.service_type ?? '').trim().toUpperCase())
    .filter(Boolean);

  return [...new Set(types)];
}

export function isServiceFilteringActive(enabledTypes) {
  return Array.isArray(enabledTypes);
}

function channelAllowed(channelKey, channelMap, enabledTypes) {
  if (!isServiceFilteringActive(enabledTypes)) return true;
  const serviceType = channelMap[channelKey];
  if (!serviceType) return true;
  return enabledTypes.includes(serviceType);
}

export function filterMenuChannelsByServices(channels, enabledTypes, options = {}) {
  const { servicesLoading = false, allowAllWhenUnknown = false } = options;
  if (servicesLoading) return [];
  if (!isServiceFilteringActive(enabledTypes)) {
    return allowAllWhenUnknown ? channels : [];
  }
  return channels.filter((channel) =>
    channelAllowed(channel.key, SERVICE_TYPE_BY_MENU_CHANNEL, enabledTypes)
  );
}

export function filterOrderChannelsByServices(channels, enabledTypes, options = {}) {
  const { servicesLoading = false, allowAllWhenUnknown = false } = options;
  if (servicesLoading) return [];
  if (!isServiceFilteringActive(enabledTypes)) {
    return allowAllWhenUnknown ? channels : [];
  }
  return channels.filter((channel) =>
    channelAllowed(channel.key, SERVICE_TYPE_BY_ORDER_CHANNEL, enabledTypes)
  );
}

export function filterDeliveryAgentOrderSources(sources, enabledTypes) {
  if (!isServiceFilteringActive(enabledTypes)) return sources;
  return sources.filter((source) => {
    const serviceType = SERVICE_TYPE_BY_DELIVERY_SOURCE[source.key];
    if (!serviceType) return true;
    return enabledTypes.includes(serviceType);
  });
}

export function getFilteredRestaurantSidebarItems(enabledTypes, sidebarItems, options = {}) {
  const { servicesLoading = false } = options;

  // Before services resolve, avoid flashing Dine-In for catering-only restaurants.
  if (!isServiceFilteringActive(enabledTypes)) {
    if (servicesLoading) {
      return sidebarItems.filter(([id]) => id !== 'dine-in');
    }
    return sidebarItems;
  }

  const menuChannels = filterMenuChannelsByServices(MENU_CHANNELS, enabledTypes);
  const orderChannels = filterOrderChannelsByServices(ORDER_CHANNELS, enabledTypes);

  return sidebarItems.filter(([id]) => {
    // Dine-In POS is only for restaurants with DINEIN service.
    // Catering create/edit lives under Orders, not the Dine-In sidebar tab.
    if (id === 'dine-in') {
      return enabledTypes.includes('DINEIN');
    }
    if (id === 'orders') return orderChannels.length > 0;
    if (id === 'restaurants') return menuChannels.length > 0;
    return true;
  });
}

export function getDefaultRestaurantHomeTab(enabledTypes, sidebarItems) {
  const items = getFilteredRestaurantSidebarItems(enabledTypes, sidebarItems);
  if (items.some(([id]) => id === 'dine-in')) return 'dine-in';
  return items[0]?.[0] || 'orders';
}

export function pickAllowedMenuChannel(requestedKey, enabledTypes, options = {}) {
  const { servicesLoading = false } = options;
  if (servicesLoading) return null;
  const allowed = filterMenuChannelsByServices(MENU_CHANNELS, enabledTypes, options);
  if (!allowed.length) return null;
  const match = allowed.find((channel) => channel.key === requestedKey);
  return match ? match.key : allowed[0].key;
}

export function pickAllowedOrderChannel(requestedKey, enabledTypes, options = {}) {
  const { servicesLoading = false } = options;
  if (servicesLoading) return null;
  const allowed = filterOrderChannelsByServices(ORDER_CHANNELS, enabledTypes, options);
  if (!allowed.length) return null;
  const normalized = requestedKey === 'online' ? 'myeats' : requestedKey;
  const match = allowed.find((channel) => channel.key === normalized);
  return match ? match.key : allowed[0].key;
}
